import { yupResolver } from '@hookform/resolvers/yup'
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'
import { LockClosedIcon } from '@heroicons/react/solid'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { API } from '../../apis/API'
import ErrorAlert from '../Alerts/ErrorAlert'
import Loader from '../Loader'
import { formatuseFormErrors, getErrorMessage, log } from '../../helpers/utils'
import { shouldSkipLoginRecaptcha } from '../../helpers/shouldSkipLoginRecaptcha'
import ReCAPTCHA from 'react-google-recaptcha'

const olcLoginRules = yup.object().shape({
    email: yup.string().email().required(),
    password: yup.string().required(),
})

const olcForgotPasswordUrl =
    process.env.NEXT_PUBLIC_OLC_FORGOT_PASSWORD_URL ||
    'https://app.openlettermarketing.com/forgot-password'
const olcSignupUrl =
    process.env.NEXT_PUBLIC_OLC_SIGNUP_URL ||
    'https://app.openlettermarketing.com/signup'

function OlcLoginForm() {
    const skipCaptcha = shouldSkipLoginRecaptcha()
    const [loading, setLoading] = useState(false)
    const [redirecting, setRedirecting] = useState(false)
    const [captchaToken, setCaptchaToken] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const recaptchaRef = useRef()

    const onCaptchaChange = (value) => {
        setCaptchaToken(value || null)
        if (recaptchaRef.current) {
            recaptchaRef.current.value = value
        }
    }

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm({ resolver: yupResolver(olcLoginRules) })

    const attemptLogin = async (data) => {
        try {
            setLoading(true)
            if (!skipCaptcha && !captchaToken) {
                throw Error('Please complete the reCAPTCHA.')
            }

            const { data: response } = await API.olcLogin({
                ...data,
                captcha: skipCaptcha ? '' : captchaToken,
            })
            if (response?.data?.redirectUrl) {
                setRedirecting(true)
                window.setTimeout(
                    () => window.location.assign(response.data.redirectUrl),
                    150,
                )
                return
            }
            throw Error('OLC login did not return a handoff URL.')
        } catch (e) {
            const message = getErrorMessage(e)
            log('error', message)
            setError('custom', { type: 'custom', message })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {loading && <Loader />}
            <form
                data-hs-cf-disabled="true"
                onSubmit={handleSubmit(attemptLogin)}
                className="hs-form-exclude space-y-3.5"
                method="POST">
                <div>
                    <label
                        htmlFor="olc-email"
                        className="mb-1.5 block text-[12.5px] font-semibold text-[#4A5160]">
                        Email
                    </label>
                    <input
                        id="olc-email"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        {...register('email')}
                        className="h-10 w-full rounded-[10px] border border-black/10 bg-[#FBFBFA] px-3 text-[14px] text-[#3F454F] placeholder:text-gray-400 transition-all focus:border-[#F28230] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F28230]/10"
                    />
                </div>

                <div>
                    <label
                        htmlFor="olc-password"
                        className="mb-1.5 block text-[12.5px] font-semibold text-[#4A5160]">
                        Password
                    </label>
                    <div className="relative">
                        <input
                            id="olc-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            {...register('password')}
                            className="h-10 w-full rounded-[10px] border border-black/10 bg-[#FBFBFA] px-3 pr-10 text-[14px] text-[#3F454F] placeholder:text-gray-400 transition-all focus:border-[#F28230] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#F28230]/10"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword((value) => !value)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7D8798] hover:text-[#4A5160]"
                            aria-label={
                                showPassword ? 'Hide password' : 'Show password'
                            }>
                            {showPassword ? (
                                <EyeOffIcon className="h-4 w-4" />
                            ) : (
                                <EyeIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                </div>

                {Object.values(errors).length > 0 && (
                    <ErrorAlert
                        error={formatuseFormErrors(errors)}
                        heading="Please fix following error(s)."
                    />
                )}

                {redirecting && (
                    <div
                        role="status"
                        aria-live="polite"
                        className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-[13px] text-green-800">
                        <p className="m-0 font-semibold">Sign in successful.</p>
                        <p className="mb-0 mt-1">
                            Redirecting you to Open Letter Connect…
                        </p>
                    </div>
                )}

                {!skipCaptcha && (
                    <div>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={
                                process.env.NEXT_PUBLIC_RECAPTCHA2_SITE_KEY
                            }
                            onChange={onCaptchaChange}
                            onExpired={() => onCaptchaChange(null)}
                            size="normal"
                            theme="light"
                        />
                    </div>
                )}

                <div className="-mt-1 flex justify-end">
                    <a
                        href={olcForgotPasswordUrl}
                        className="text-[12.5px] text-[#6F7785] hover:text-[#ED5C2F] hover:underline">
                        Forgot password?
                    </a>
                </div>

                <button
                    type="submit"
                    disabled={
                        (!skipCaptcha && !captchaToken) ||
                        loading ||
                        redirecting
                    }
                    className={`relative mt-1 flex w-full justify-center rounded-full px-4 py-3 text-[14px] font-semibold text-white transition-all shadow-sm ${
                        (!skipCaptcha && !captchaToken) ||
                        loading ||
                        redirecting
                            ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                            : 'bg-gradient-to-r from-[#ED5C2F] to-[#F28230] hover:-translate-y-0.5 hover:brightness-105 hover:shadow-md'
                    }`}>
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <LockClosedIcon
                            className="h-5 w-5"
                            aria-hidden="true"
                        />
                    </span>
                    {redirecting ? 'Redirecting…' : 'Sign in to OLC'}
                </button>
            </form>
            <div className="mt-[18px] text-center text-[13px] text-[#6F7785]">
                New to OLC?{' '}
                <a
                    href={olcSignupUrl}
                    className="font-semibold text-[#ED5C2F] hover:underline">
                    Create an account
                </a>
            </div>
        </>
    )
}

export default OlcLoginForm
